import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { apiUrl } from './utils/api';

function ManagementSetup() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const alreadyUnlocked = useMemo(() => {
    const token = localStorage.getItem('management_token');
    const expires = localStorage.getItem('management_expires');
    const expired = expires && Date.now() > parseInt(expires, 10);

    if (expired) {
      localStorage.removeItem('management_token');
      localStorage.removeItem('management_expires');
      return false;
    }

    return Boolean(token);
  }, []);

  useEffect(() => {
    if (alreadyUnlocked) {
      navigate('/settings/management-hub', { replace: true });
    }
  }, [alreadyUnlocked, navigate]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(apiUrl('/api/management/unlock'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Invalid password');
        setLoading(false);
        return;
      }

      // Store token in localStorage
      localStorage.setItem('management_token', data.token);
      localStorage.setItem('management_expires', data.expires_at);

      // Redirect to management hub
      navigate('/settings/management-hub');
    } catch (err) {
      setError('Connection error. Is the server running?');
      setLoading(false);
    }
  };

  if (alreadyUnlocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Management Area"
          subtitle="Opening protected area"
          backTo="/admin"
          backLabel="Administration"
          sticky
        />
        <main className="max-w-lg mx-auto px-6 py-12">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900">Opening Management Hub...</h2>
            <p className="text-sm text-gray-600 mt-2">Your management session is active.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Management Area"
        subtitle="Restricted Access"
        backTo="/admin"
        backLabel="Administration"
        sticky
      />

      <main className="max-w-lg mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-900">Management Hub</h2>
            <p className="text-gray-600 mt-2">
              Enter the master password to access platform management
            </p>
          </div>

          <form onSubmit={handleUnlock}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Master Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                placeholder="Enter master password..."
                autoFocus
                disabled={loading}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              {loading ? 'Unlocking...' : 'Unlock Management'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              This area is restricted to authorized personnel only.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default ManagementSetup;
